import CreateIcon from '@mui/icons-material/Create';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { alpha } from '@mui/material/styles';
import { useCurrentTenant } from '@qpr/client-library';
import ErrorObject from '@qpr/client-library/dist/esm/ErrorContext/ErrorObject';
import axios, { AxiosError, AxiosResponse } from 'axios';
import React, { useContext } from 'react';
import { useQuery } from 'react-query';
import { Link, useLocation } from 'react-router-dom';
import AppBarContext from '../../../components/BusinessPortalAppBarContext';

interface Data {
  name?: string;
  attributes?: { accesslevel?: string; inputtype?: string };
  elements?: Data[];
}

interface Period {
  islocked: string;
}

type JsonString = string;

const hasCorrectAccessLevel = (array: Array<string>) =>
  array.some(item => item === '1' || item === '2');

const hasManualInputType = (array: Array<string>) => array.some(item => item === '0');

const isPeriodLocked = (period: Period) => period.islocked === '1';

const getAccessLevels = (carry: Array<string>, { attributes, elements }: Data): Array<string> => {
  const withChildren =
    elements && elements?.length > 0 ? elements?.reduce(getAccessLevels, carry) : carry;
  return attributes?.accesslevel ? [attributes.accesslevel, ...withChildren] : [...withChildren];
};

const getSeriesInputTypes = (
  carry: Array<string>,
  { elements, attributes, name }: Data
): Array<string> => {
  const withChildren =
    elements && elements?.length > 0 ? elements.reduce(getSeriesInputTypes, carry) : carry;
  return name === 'series' && attributes?.inputtype
    ? [attributes.inputtype, ...withChildren]
    : [...withChildren];
};

const shouldShowButton = (data: JsonString, period: Period) => {
  const parsedData = JSON.parse(data) as Data;

  const seriesInputTypes = getSeriesInputTypes([], parsedData);
  const accessLevels = getAccessLevels([], parsedData);

  const showInputButton =
    !isPeriodLocked(period) &&
    hasManualInputType(seriesInputTypes) &&
    hasCorrectAccessLevel(accessLevels);

  return showInputButton;
};

const ValueInputButton = () => {
  const { selectedPeriod, valueInputNavigationTarget } = useContext(AppBarContext);
  const { search } = useLocation();
  const { symbol } = useCurrentTenant();

  const queryString = new URLSearchParams(search);

  if (valueInputNavigationTarget && valueInputNavigationTarget.includes('viewdefinitionId')) {
    queryString.delete('viewdefinitionId');
  }

  const meaId = queryString.get('meaId');

  const { isLoading, data } = useQuery<AxiosResponse<any>, AxiosError<ErrorObject>>(
    `kpi-input-${meaId}`,
    async () => {
      const response = await axios.post('meaquery', {
        query: `[${meaId}].childobjects`,
        attributes: `accesslevel, seriessettings`,
        options: 'enumAsNumber=1',
        jsonmode: 2
      });

      return response.data;
    },
    {
      enabled: !!meaId && !!selectedPeriod && selectedPeriod?.islocked === '0'
    }
  );

  if (!valueInputNavigationTarget) return null;

  if (!isLoading && data?.data && shouldShowButton(data.data, selectedPeriod)) {
    return (
      <Card elevation={0} style={{ backgroundColor: 'transparent' }}>
        <CardContent style={{ float: 'right' }}>
          <Link
            to={`/${symbol}${valueInputNavigationTarget}${
              valueInputNavigationTarget.includes('?') ? '&' : '?'
            }${queryString.toString()}`}
            style={{ textDecoration: 'none' }}
            data-testid="value-input-button-link"
          >
            <Button
              sx={{
                color: 'primary.contrastText',
                borderColor: 'primary.contrastText',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: theme => alpha(theme.palette.grey[900], 0.04)
                }
              }}
              variant="outlined"
              startIcon={<CreateIcon />}
            >
              Input values
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default ValueInputButton;
